import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { PagedResponse } from '@core/models/models';
import {
  ManutencaoEquipamento,
  ManutencaoEquipamentoFilters,
  ManutencaoEquipamentoForm,
} from '../models/manutencao-equipamento.model';

@Injectable({ providedIn: 'root' })
export class ManutencaoEquipamentoService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiurl}/api/ManutencaoEquipamento`;

  listar(
    filters: ManutencaoEquipamentoFilters = {},
  ): Observable<PagedResponse<ManutencaoEquipamento>> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<PagedResponse<ManutencaoEquipamento>>(this.baseUrl, {
      params,
    });
  }

  obter(id: number): Observable<ManutencaoEquipamento> {
    return this.http.get<ManutencaoEquipamento>(`${this.baseUrl}/${id}`);
  }

  obterIndicadores(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Indicadores`);
  }

  criar(
    form: ManutencaoEquipamentoForm,
    anexos: File[] = [],
  ): Observable<ManutencaoEquipamento> {
    return this.http.post<ManutencaoEquipamento>(
      this.baseUrl,
      this.toFormData(form, anexos),
    );
  }

  atualizar(
    id: number,
    form: ManutencaoEquipamentoForm,
  ): Observable<ManutencaoEquipamento> {
    const body = this.toFormData(form);
    body.append('Id', String(id));
    return this.http.put<ManutencaoEquipamento>(this.baseUrl, body);
  }

  eliminar(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  adicionarAnexo(
    manutencaoId: number,
    ficheiro: File,
    descricao: string,
  ): Observable<unknown> {
    const body = new FormData();
    body.append('Ficheiro', ficheiro, ficheiro.name);
    body.append('DescricaoFicheiro', descricao || ficheiro.name);
    return this.http.post(
      `${this.baseUrl}/${manutencaoId}/Anexos`,
      body,
    );
  }

  substituirAnexo(
    manutencaoId: number,
    anexoManutencaoId: number,
    ficheiro: File,
  ): Observable<unknown> {
    const body = new FormData();
    body.append('NovoFicheiro', ficheiro, ficheiro.name);
    return this.http.put(
      `${this.baseUrl}/${manutencaoId}/Anexos/${anexoManutencaoId}`,
      body,
    );
  }

  eliminarAnexo(
    manutencaoId: number,
    anexoManutencaoId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${manutencaoId}/Anexos/${anexoManutencaoId}`,
    );
  }

  private toFormData(
    form: ManutencaoEquipamentoForm,
    anexos: File[] = [],
  ): FormData {
    const body = new FormData();
    if (form.equipamentoId) {
      body.append('EquipamentoId', String(form.equipamentoId));
    }
    const dataRef = form.dataManutencao ?? form.dataEntrada;
    body.append('DataManutencao', this.formatDate(dataRef));
    if (form.dataEntrada) {
      body.append('DataEntrada', this.formatDate(form.dataEntrada));
    }
    if (form.dataSaida) {
      body.append('DataSaida', this.formatDate(form.dataSaida));
    }
    if (form.dataPrevisaoSaida) {
      body.append('DataPrevisaoSaida', this.formatDate(form.dataPrevisaoSaida));
    }
    if (form.manutencaoTipoEnum !== null && form.manutencaoTipoEnum !== undefined) {
      body.append('ManutencaoTipoEnum', String(form.manutencaoTipoEnum));
    }
    if (form.manutencaoEstadoEnum !== null && form.manutencaoEstadoEnum !== undefined) {
      body.append('ManutencaoEstadoEnum', String(form.manutencaoEstadoEnum));
    }
    const custoFinal = form.custo ?? form.custoReal ?? form.custoEstimado ?? 0;
    body.append('Custo', String(custoFinal));
    if (form.custoEstimado !== null && form.custoEstimado !== undefined) {
      body.append('CustoEstimado', String(form.custoEstimado));
    }
    if (form.custoReal !== null && form.custoReal !== undefined) {
      body.append('CustoReal', String(form.custoReal));
    }
    if (form.oficina) {
      body.append('Oficina', form.oficina.trim());
    }
    if (form.oficinaContacto) {
      body.append('OficinaContacto', form.oficinaContacto.trim());
    }
    body.append('DescricaoServico', (form.descricaoServico || '').trim());
    body.append('Observacoes', (form.observacoes || '').trim());
    body.append(
      'QuilometragemOuHorasUso',
      String(form.quilometragemOuHorasUso ?? 0),
    );
    anexos.forEach((file) => body.append('Anexos', file, file.name));
    return body;
  }

  private formatDate(value: Date | string | null | undefined): string {
    if (!value) return '';
    try {
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }
}
